// src/components/common/ui/Modal.js
import React from 'react';

/**
 * Envoltorio de modal reutilizable: backdrop con blur + panel centrado,
 * cierre por clic fuera / Escape y cabecera opcional con botón de cerrar.
 * Reemplaza el `fixed inset-0 z-50 …` repetido en varios modales.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} [titulo]
 * @param {string} [subtitulo]
 * @param {'sm'|'md'|'lg'|'xl'} [size]
 * @param {React.ReactNode} [footer]
 */
const ANCHOS = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
};

export default function Modal({ open, onClose, titulo, subtitulo, size = 'md', footer, children }) {
    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
            style={{ margin: 0 }}
            // Cierra solo si el clic cae en el fondo (no en el panel), evitando el
            // stopPropagation que ESLint a11y penaliza.
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
            role="button"
            tabIndex={-1}
            aria-label="Cerrar modal"
        >
            <div
                className={`w-full ${ANCHOS[size] || ANCHOS.md} bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]`}
                role="dialog"
                aria-modal="true"
            >
                {titulo && (
                    <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50 shrink-0">
                        <div className="min-w-0">
                            <h2 className="text-lg font-black text-slate-900 tracking-tight truncate">{titulo}</h2>
                            {subtitulo && <p className="text-xs font-semibold text-slate-400 truncate">{subtitulo}</p>}
                        </div>
                        {onClose && (
                            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        )}
                    </div>
                )}

                <div className="p-6 overflow-y-auto flex-1">
                    {children}
                </div>

                {footer && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
